import { notification } from 'antd';
import { DriveItem } from '../../../models/Drive';
import { User } from '../../../models/User';
import { GroupId, ProjectId } from '../../../models/Types';
import { apiFetchDocument, apiFetchDocumentWithCaching } from '../api';
import i18n from '../../../i18n';
import { getDriveItemType, getPDFType } from '../util';
import { useSelector } from 'react-redux';
import {
  getDocumentSettings,
  getDocumentsCachingEnabled,
  getUserMe,
} from '../../../apps/main/rootReducer';
import { useCallback } from 'react';

/**
 * DriveItem-Typen, die sich über das native MS-Office-URI-Schema
 * (`<type>:ofe|ofc|u|<webDavUrl>`) lokal öffnen lassen.
 */
const OFFICE_PROTOCOL_TYPES = [
  'ms-word',
  'ms-excel',
  'ms-powerpoint',
  'ms-project',
];

/**
 * Dateiendungen, die explizit lokal via OneDrive (`odopen://`) im lokalen
 * Standardprogramm geöffnet werden sollen. Alles andere ohne dedizierte
 * Behandlung (onenote, visio, pdf, Office) öffnet im Browser.
 */
const LOCAL_OPEN_EXTENSIONS = [
  // Bilder
  'png',
  'jpg',
  'jpeg',
  'gif',
  'bmp',
  'tif',
  'tiff',
  'svg',
  'webp',
  'heic',
  // Adobe
  'psd',
  'ai',
  'indd',
  'eps',
  'xd',
  'aep',
  'prproj',
  // E-Mail
  'eml',
  'msg',
];

const isLocalOpenExtension = (fileName?: string): boolean => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  return !!ext && LOCAL_OPEN_EXTENSIONS.includes(ext);
};

/**
 * Baut eine `odopen://`-URL, mit der OneDrive die Datei im lokalen
 * Standardprogramm öffnet. Alle Parameter stammen aus `sharepointIds` des
 * DriveItems sowie dem angemeldeten Benutzer.
 * Gibt `null` zurück, wenn erforderliche Felder fehlen.
 */
const buildOneDriveOpenUrl = (item: DriveItem, me?: User): string | null => {
  const sharepointIds = item.sharepointIds;
  const fileId = sharepointIds?.listItemUniqueId;
  const siteId = sharepointIds?.siteId;
  const listId = sharepointIds?.listId;
  const webUrl = sharepointIds?.siteUrl;
  if (!fileId || !siteId || !listId || !webUrl || !item.name) {
    return null;
  }
  const params = new URLSearchParams({
    fileId,
    siteId: `{${siteId}}`,
    listId: `{${listId}}`,
    userEmail: me?.mail ?? '',
    userId: me?.id ?? '',
    webUrl,
    fileName: item.name,
  });
  return `odopen://openFile/?${params.toString()}`;
};

export const useGetLocalUrlByDriveId = () => {
  const me = useSelector(getUserMe);
  const documentSettings = useSelector(getDocumentSettings);
  const isDocumentsCachingEnabled =
    useSelector(getDocumentsCachingEnabled) ?? false;

  const getLocalUrlByDriveItem = useCallback(
    async (
      groupId: GroupId,
      projectId: ProjectId,
      driveItem: DriveItem,
      setIsFetching?: (value: boolean) => void,
      openPdf?: boolean
    ): Promise<string> => {
      let item: DriveItem = driveItem;
      if (!driveItem.parentReference?.path) {
        if (setIsFetching) {
          setIsFetching(true);
        }
        const { data } =
          isDocumentsCachingEnabled === true
            ? await apiFetchDocumentWithCaching(projectId, driveItem.id)
            : await apiFetchDocument(groupId, driveItem.id);
        if (setIsFetching) {
          setIsFetching(false);
        }
        if (data) {
          item = data;
        } else {
          notification.open({
            message: i18n.t('common:error'),
            description: i18n.t(
              'documents:errorMessages.fetchDriveItemErrorLink'
            ),
          });
          return null;
        }
      }
      const type = getDriveItemType(item);
      // TODO: check if this pdf type is needed - otherwise delete
      const pdfType = getPDFType(item);
      // Allowlist (Bilder, Adobe, EML/MSG): lokal via OneDrive öffnen.
      // Fallback auf Browser, falls sharepointIds fehlen.
      if (isLocalOpenExtension(item.name)) {
        return buildOneDriveOpenUrl(item, me) ?? item.webUrl;
      }
      if (item.webDavUrl) {
        if (type) {
          if (type === 'onenote') {
            return `${type}:${item.webDavUrl}`;
          }
          if (type === 'ms-visio') {
            return item.webUrl?.replace('action=default', 'action=edit');
          }
          if (type === 'pdf') {
            if (documentSettings.defaultPdfProgram === 'pdfXchange') {
              if (openPdf !== undefined && openPdf) {
                return `prio365://openpdf=${item.webDavUrl}`;
              }
            }
            if (documentSettings.defaultPdfProgram === 'adobeAcrobat') {
              if (openPdf !== undefined && openPdf) {
                return `acrobat2018://openConnectorFile/?storageId=sharepoint&${
                  !!me?.mail ? `userId=${me?.mail}&` : ''
                }fileId=${item.webDavUrl}`;
              }
            }
            if (documentSettings.defaultPdfProgram === 'oneDrive') {
              if (openPdf !== undefined && openPdf) {
                const oneDriveUrl = buildOneDriveOpenUrl(item, me);
                if (oneDriveUrl) {
                  return oneDriveUrl;
                }
              }
            }
          }
          if (OFFICE_PROTOCOL_TYPES.includes(type)) {
            return `${type}:ofe|ofc|u|${item.webDavUrl}`;
          }
          // Nicht in der Allowlist und kein Office/onenote/visio/pdf:
          // im Browser öffnen.
          return item.webUrl;
        }

        // TODO: check if this pdf type is needed - otherwise delete
        if (pdfType) {
          if (documentSettings.defaultPdfProgram === 'pdfXchange') {
            if (openPdf !== undefined && openPdf) {
              return `prio365://openpdf=${item.webDavUrl}`;
            }
          }
          if (documentSettings.defaultPdfProgram === 'adobeAcrobat') {
            if (openPdf !== undefined && openPdf) {
              return `acrobat2018://openConnectorFile/?storageId=sharepoint&${
                !!me?.mail ? `userId=${me?.mail}&` : ''
              }fileId=${item.webDavUrl}`;
            }
          }
          if (documentSettings.defaultPdfProgram === 'oneDrive') {
            if (openPdf !== undefined && openPdf) {
              const oneDriveUrl = buildOneDriveOpenUrl(item, me);
              if (oneDriveUrl) {
                return oneDriveUrl;
              }
            }
          }
        }
      }

      // Unbekannter Typ und nicht in der Allowlist: im Browser öffnen.
      return item.webUrl;
    },
    [documentSettings.defaultPdfProgram, me, isDocumentsCachingEnabled]
  );

  return {
    getLocalUrlByDriveItem,
  };
};
